import { API_BASE } from '../config';

export interface CheckUrlRequest {
  url: string;
  deep?: boolean;
  skipDnsCheck?: boolean;
}

export interface BrandImpersonation {
  classification: 'OK' | 'Suspicious' | 'Scam';
  score: number;
  reason: string;
  closestTrustedDomain?: string;
  matchedBrand?: string;
  typoDistance?: number;
  jaroWinkler?: number;
  idnHomographSuspected?: boolean;
  punycodeHostname?: string;
  signals: string[];
}

export interface CheckUrlResponse {
  normalizedUrl: string;
  verdict: 'Safe' | 'Suspicious' | 'Unknown';
  confidence: number;
  evidence?: string[];
  sources?: Array<{
    sourceName: string;
    sourceUrl: string;
    lastUpdatedAt?: string;
  }>;
  checkedAt: string;
  brandImpersonation?: BrandImpersonation;
}

export async function checkUrl(request: CheckUrlRequest): Promise<CheckUrlResponse> {
  const res = await fetch(`${API_BASE}/v1/check-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export interface ImpersonationDomain {
  id: number;
  observedDomain: string;
  exampleUrl: string;
  firstSeenAt: string;
  lastSeenAt: string;
  hitCount: number;
  matchedBrandCode?: string;
  matchedBrandName?: string;
  closestTrustedDomain?: string;
  typoDistance?: number;
  jaroWinkler?: number;
  idnHomographSuspected?: boolean;
  punycodeHostname?: string;
  signals: string[];
  finalScore: number;
  reason: string;
  status: 'suspected' | 'confirmed' | 'dismissed';
}

export interface ImpersonationDomainsResponse {
  domains: ImpersonationDomain[];
  count: number;
  filters: {
    brandCode: string | null;
    status: string | null;
  };
  pagination: {
    limit: number;
    offset: number;
  };
}

export async function getImpersonationDomains(params?: {
  brandCode?: string;
  status?: 'suspected' | 'confirmed' | 'dismissed';
  limit?: number;
  offset?: number;
}): Promise<ImpersonationDomainsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.brandCode) queryParams.set('brandCode', params.brandCode);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.offset) queryParams.set('offset', params.offset.toString());

  const url = `${API_BASE}/v1/impersonation-domains${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const res = await fetch(url);

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }

  return res.json();
}

// SMS Analysis API
export interface SmsAnalyzeRequest {
  content: string;
  sender?: string;
  skipUrlCheck?: boolean;
}

export interface AdviceStep {
  id: string;
  type: 'action' | 'info' | 'warning' | 'link';
  labelKey: string;
  link?: string;
  priority: number;
}

export interface SmsAnalyzeResponse {
  riskLevel: 'safe' | 'inconclusive' | 'suspicious' | 'high_risk' | 'critical';
  confidence: number;
  summary: {
    titleKey: string;
    messageKey: string;
    params?: Record<string, string>;
  };
  highlights?: {
    urls: string[];
    brands: string[];
    threats: string[];
  };
  guidance: {
    prevention: AdviceStep[];
    remediation: AdviceStep[];
  };
  analysis?: {
    reasonItems: Array<{
      labelKey: string;
      params?: Record<string, string>;
    }>;
    mappedScamTypeKey?: string;
    mappedTacticKeys?: string[];
  };
  metadata?: {
    aiUnavailable?: boolean;
  };
}

export async function analyzeSms(request: SmsAnalyzeRequest): Promise<SmsAnalyzeResponse> {
  const res = await fetch(`${API_BASE}/v1/sms/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }

  return (await res.json()) as SmsAnalyzeResponse;
}
